import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWebsiteSettings } from '../../context/WebsiteSettingsContext';
import './StaticPages.css';

const API = process.env.REACT_APP_API_URL || '/api';

interface SubjectOption { value: string; label: string; label_bn?: string; }

interface ContactData {
  hero_title: string; hero_title_bn?: string;
  hero_subtitle: string; hero_subtitle_bn?: string;
  location: string; location_bn?: string;
  email: string;
  business_hours: string; business_hours_bn?: string;
  form_title: string; form_title_bn?: string;
  subject_options: SubjectOption[];
}

interface FormState { name: string; email: string; subject: string; message: string; }

const Contact: React.FC = () => {
  const { settings } = useWebsiteSettings();
  const { i18n } = useTranslation();
  const isBn = i18n.language === 'bn';
  const siteName = settings?.site_name || 'Our Store';
  const supportPhone = settings?.support_phone || '';
  const facebookUrl = settings?.facebook_url || '';

  const [cms, setCms] = useState<ContactData | null>(null);
  const [form, setForm] = useState<FormState>({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetch(`${API}/pages/contact/`)
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setCms(d))
      .catch(() => {});
  }, []);

  const t = (en: string | undefined, bn: string | undefined, fallback: string) =>
    isBn ? (bn || en || fallback) : (en || fallback);

  const hero_title = t(cms?.hero_title, cms?.hero_title_bn, 'Contact Us');
  const hero_subtitle = t(cms?.hero_subtitle, cms?.hero_subtitle_bn, "Have a question or need help? We're here and ready to assist you.");
  const location = t(cms?.location, cms?.location_bn, 'Dhaka, Bangladesh');
  const emailAddr = cms?.email || `support@${siteName.toLowerCase().replace(/\s+/g, '')}.com`;
  const business_hours = t(cms?.business_hours, cms?.business_hours_bn, 'Saturday \u2013 Thursday: 9 AM \u2013 9 PM\nFriday: 2 PM \u2013 9 PM');
  const form_title = t(cms?.form_title, cms?.form_title_bn, 'Send Us a Message');
  const subject_options = cms?.subject_options || [
    { value: 'order', label: 'Order Inquiry' },
    { value: 'return', label: 'Return / Exchange' },
    { value: 'product', label: 'Product Question' },
    { value: 'payment', label: 'Payment Issue' },
    { value: 'other', label: 'Other' },
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    await new Promise(res => setTimeout(res, 1000));
    setLoading(false);
    setSubmitted(true);
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="static-page">
      <div className="static-hero">
        <h1>{hero_title}</h1>
        <p>{hero_subtitle}</p>
      </div>

      <div className="static-content">
        <div className="contact-layout">
          <div className="contact-info-block">
            <div className="contact-info-item">
              <span className="contact-icon">{'\uD83D\uDCCD'}</span>
              <div>
                <h4>{isBn ? '\u0986\u09AE\u09BE\u09A6\u09C7\u09B0 \u09B2\u09CB\u0995\u09C7\u09B6\u09A8' : 'Our Location'}</h4>
                <p>{location}</p>
              </div>
            </div>

            {supportPhone && (
              <div className="contact-info-item">
                <span className="contact-icon">{'\uD83D\uDCDE'}</span>
                <div>
                  <h4>{isBn ? '\u09AB\u09CB\u09A8 / \u09B9\u09CB\u09AF\u09BC\u09BE\u099F\u09B8\u0985\u09CD\u09AF\u09BE\u09AA' : 'Phone / WhatsApp'}</h4>
                  <a href={`tel:${supportPhone}`}>{supportPhone}</a>
                </div>
              </div>
            )}

            <div className="contact-info-item">
              <span className="contact-icon">{'\u2709\uFE0F'}</span>
              <div>
                <h4>{isBn ? '\u0987\u09AE\u09C7\u0987\u09B2 \u09B8\u09BE\u09AA\u09CB\u09B0\u09CD\u099F' : 'Email Support'}</h4>
                <a href={`mailto:${emailAddr}`}>{emailAddr}</a>
              </div>
            </div>

            <div className="contact-info-item">
              <span className="contact-icon">{'\uD83D\uDD50'}</span>
              <div>
                <h4>{isBn ? '\u0985\u09AB\u09BF\u09B8 \u09B8\u09AE\u09AF\u09BC' : 'Business Hours'}</h4>
                {business_hours.split('\n').map((line, i) => <p key={i}>{line}</p>)}
              </div>
            </div>

            {facebookUrl && (
              <div className="contact-info-item">
                <span className="contact-icon">{'\uD83D\uDCAC'}</span>
                <div>
                  <h4>{isBn ? '\u09AB\u09C7\u09B8\u09AC\u09C1\u0995 \u09AA\u09C7\u0987\u099C' : 'Facebook Page'}</h4>
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer">{isBn ? '\u09AB\u09C7\u09B8\u09AC\u09C1\u0995\u09C7 \u09AE\u09C7\u09B8\u09C7\u099C \u0995\u09B0\u09C1\u09A8' : 'Message us on Facebook'}</a>
                </div>
              </div>
            )}
          </div>

          <div className="contact-form-card">
            <h3>{form_title}</h3>
            {submitted ? (
              <div className="form-success">{isBn ? '\u2705 \u09A7\u09A8\u09CD\u09AF\u09AC\u09BE\u09A6! \u0986\u09AA\u09A8\u09BE\u09B0 \u09AC\u09BE\u09B0\u09CD\u09A4\u09BE \u09AA\u09BE\u0993\u09AF\u09BC\u09BE \u0997\u09C7\u099B\u09C7\u0964' : '\u2705 Thank you! Your message has been received. We\'ll get back to you shortly.'}</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="form-field">
                  <label htmlFor="name">{isBn ? '\u0986\u09AA\u09A8\u09BE\u09B0 \u09A8\u09BE\u09AE' : 'Your Name'}</label>
                  <input id="name" name="name" type="text" placeholder={isBn ? '\u09AF\u09C7\u09AE\u09A8: \u09B0\u09B9\u09BF\u09AE \u0986\u09B9\u09AE\u09C7\u09A6' : 'e.g. Rahim Ahmed'} value={form.name} onChange={handleChange} required />
                </div>
                <div className="form-field">
                  <label htmlFor="email">{isBn ? '\u0987\u09AE\u09C7\u0987\u09B2 \u09A0\u09BF\u0995\u09BE\u09A8\u09BE' : 'Email Address'}</label>
                  <input id="email" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange} required />
                </div>
                <div className="form-field">
                  <label htmlFor="subject">{isBn ? '\u09AC\u09BF\u09B7\u09AF\u09BC' : 'Subject'}</label>
                  <select id="subject" name="subject" value={form.subject} onChange={handleChange} required>
                    <option value="">{isBn ? '\u098F\u0995\u099F\u09BF \u09AC\u09BF\u09B7\u09AF\u09BC \u09A8\u09BF\u09B0\u09CD\u09AC\u09BE\u099A\u09A8 \u0995\u09B0\u09C1\u09A8\u2026' : 'Select a topic\u2026'}</option>
                    {subject_options.map((opt, i) => (
                      <option key={i} value={opt.value}>{isBn ? (opt.label_bn || opt.label) : opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-field">
                  <label htmlFor="message">{isBn ? '\u09AC\u09BE\u09B0\u09CD\u09A4\u09BE' : 'Message'}</label>
                  <textarea id="message" name="message" placeholder={isBn ? '\u0986\u09AA\u09A8\u09BE\u09B0 \u09B8\u09AE\u09B8\u09CD\u09AF\u09BE \u09AC\u09BF\u09B8\u09CD\u09A4\u09BE\u09B0\u09BF\u09A4 \u09B2\u09BF\u0996\u09C1\u09A8\u2026' : 'Describe your issue or question in detail\u2026'} value={form.message} onChange={handleChange} required />
                </div>
                <button className="submit-btn" type="submit" disabled={loading}>{loading ? (isBn ? '\u09AA\u09BE\u09A0\u09BE\u09A8\u09CB \u09B9\u099A\u09CD\u099B\u09C7\u2026' : 'Sending\u2026') : (isBn ? '\u09AC\u09BE\u09B0\u09CD\u09A4\u09BE \u09AA\u09BE\u09A0\u09BE\u09A8' : 'Send Message')}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
